import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Button, Message, Spin, UploadOutlined } from '@fx-ui/fine-design';
import Intl from '@fx-ui/intl';
import EmptyImg from '~/assets/empty_status/drawer_empty.png';
import { COLOR_VAR, MAX_ICON_SIZE } from '~/constants';
import { dataURLtoBlob, validExt } from '~/utils';

export interface CropImage {
    url?: string;
    imageUrl?: string;
    cropBoxData?: any;
    imageBlob?: Blob;
}

interface CropSize {
    width: number | string;
    height: number | string;
}

export interface CropBox {
    width: number;
    height: number;
    top: number;
    left: number;
}

interface CropperUploadProps {
    imageUrl?: string;
    cropBoxData?: CropBox;
    fileVolume?: number;
    size?: CropSize;
    onImageChange: (icon: CropImage) => void;
}

/** 图片裁剪上传组件 */
const CropperUpload: React.FC<CropperUploadProps> = ({
    imageUrl,
    cropBoxData,
    fileVolume = MAX_ICON_SIZE,
    size,
    onImageChange
}) => {
    const [loading, setLoading] = useState(!!imageUrl);
    const cropperRef = useRef<any>();
    const inputRef = useRef<any>();
    const imageRef = useRef<CropImage>({
        imageUrl,
        cropBoxData
    });

    const fileVolumeMB = fileVolume / (1024 * 1024);

    const cropChange = (url?: string) => {
        if (url) {
            onImageChange({
                url,
                imageUrl: url
            });
            return;
        }
        const cropper = cropperRef.current?.cropper;
        if (!cropper) {
            return;
        }
        const boxData = cropper.getCropBoxData();
        imageRef.current.cropBoxData = boxData;
        const canvas = cropper.getCroppedCanvas({
            fillColor: COLOR_VAR.bgContainer,
            width: 200,
            height: 200
        });
        if (!canvas) {
            return;
        }
        const cropUrl = canvas.toDataURL('image/jpeg');
        const imageBlob = dataURLtoBlob(cropUrl);
        onImageChange({
            url: imageRef.current.imageUrl,
            cropBoxData: boxData,
            imageUrl: cropUrl,
            imageBlob
        });
    };

    const imgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        const file = input.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > fileVolume) {
            Message.add({
                messageType: 'toast',
                message: Intl.get('image.upload.beyong.limit', { limit: fileVolumeMB }),
                type: 'warning'
            });
            input.value = '';
            return;
        }
        if (!validExt(file, ['image/jpeg', 'image/jpg', 'image/png'])) {
            Message.add({
                messageType: 'toast',
                message: Intl.get('upload.notSupportFileTip'),
                type: 'warning'
            });
            input.value = '';
            return;
        }
        const url = URL.createObjectURL(file);
        imageRef.current = { imageUrl: url };
        cropChange(url);
        setLoading(true);
    };

    return (
        <div className="cropper-upload-pane">
            <div
                className={classNames('upload-pane', {
                    'pdt-70': !imageRef.current.imageUrl
                })}
            >
                {loading && <Spin />}
                {imageRef.current.imageUrl ? (
                    <>
                        <Cropper
                            className="image-crop"
                            ref={cropperRef}
                            src={imageRef.current.imageUrl}
                            style={{
                                width: 300,
                                height: 234
                            }}
                            dragMode="none"
                            aspectRatio={1}
                            autoCropArea={1}
                            viewMode={1}
                            minCropBoxWidth={24}
                            minCropBoxHeight={24}
                            zoomable={false}
                            checkOrientation={false}
                            ready={() => {
                                const {
                                    cropBoxData: currentCropBoxData,
                                    imageUrl: currentImageUrl
                                } = imageRef.current;
                                if (currentCropBoxData && currentImageUrl) {
                                    cropperRef.current?.cropper?.setCropBoxData(currentCropBoxData);
                                }
                                cropChange();
                                setLoading(false);
                            }}
                            cropend={() => {
                                cropChange();
                            }}
                        />
                        <Button
                            type="normal"
                            icon={<UploadOutlined />}
                            onClick={() => {
                                if (inputRef.current) {
                                    inputRef.current.click();
                                }
                            }}
                        >
                            {Intl.get('modify.upload.replace')}
                        </Button>
                    </>
                ) : (
                    <>
                        <img width="60" src={EmptyImg} />
                        <div className="upload-tip">{Intl.get('modify.upload.undo')}</div>
                    </>
                )}
                <div className="upload-tip">
                    {Intl.get('image.upload.format.tip', { limit: fileVolumeMB })}
                </div>
                <div className="upload-tip">
                    {Intl.get(
                        'image.upload.size.tip',
                        (size as any) || {
                            width: 200,
                            height: 200
                        }
                    )}
                </div>
                {!imageRef.current.imageUrl && (
                    <Button
                        type="normal"
                        icon={<UploadOutlined />}
                        onClick={() => {
                            if (inputRef.current) {
                                inputRef.current.click();
                            }
                        }}
                    >
                        {Intl.get('modify.upload.icon')}
                    </Button>
                )}
                <input
                    ref={inputRef}
                    onChange={imgChange}
                    style={{ display: 'none' }}
                    type="file"
                    accept="image/jpg,image/jpeg,image/png"
                />
            </div>
        </div>
    );
};

export default CropperUpload;
